import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../services/api/axios.config';
import { notify } from '../utils/methods/notification';

const initialState = {
    isLoading: false,
    allRemanufacturingReasons: [],
    addModal: false
};

// fetch all remanufacturing reasons
export const fetchAllRemanufacturingReasons = createAsyncThunk(
    'bmr/fetchAllRemanufacturingReasons',
    async (_, { rejectWithValue }) => {
        try {
            const response = await api.get('/remanufacturing_reasons');
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response ? error.response.data : error.message);
        }
    }
);

// add remanufacturing reason
export const addRemanufacturingReason = createAsyncThunk(
    'bmr/addRemanufacturingReason',
    async (args, { rejectWithValue, dispatch }) => {
        try {
            const response = await api.post('/remanufacturing_reasons/create', args);
            notify.success(response.data.message);
            dispatch(disableAddModal());
            dispatch(fetchAllRemanufacturingReasons());
            return response.data;
        } catch (error) {
            notify.error(error || error.message);
            return rejectWithValue(error.response ? error.response.data : error.message);
        }
    }
);

// edit remanufacturing reason
export const editRemanufacturingReason = createAsyncThunk(
    'bmr/editRemanufacturingReason',
    async (args, { rejectWithValue, dispatch }) => {
        try {
            const { data, editId } = args;
            const response = await api.put(`/remanufacturing_reasons/update/${editId}`, data);
            notify.success(response.data.message);
            dispatch(disableAddModal());
            dispatch(fetchAllRemanufacturingReasons());
            return response.data;
        } catch (error) {
            notify.error(error?.message || error);
            return rejectWithValue(error.response ? error.response.data : error.message);
        }
    }
);

// delete remanufacturing reason
export const deleteRemanufacturingReason = createAsyncThunk(
    'bmr/deleteRemanufacturingReason',
    async (args, { rejectWithValue, dispatch }) => {
        try {
            const { id } = args;
            const response = await api.delete(`/remanufacturing_reasons/${id}`);
            notify.success(response.data.message);
            dispatch(fetchAllRemanufacturingReasons());
            return response.data;
        } catch (error) {
            notify.error(error?.message || error);
            return rejectWithValue(error.response ? error.response.data : error.message);
        }
    }
);

// @info get Esential patient for the portal 1
export const fetchRemanufacturingReasonDropDown = createAsyncThunk(
    'bmr/fetchRemanufacturingReasonDropDown',
    async (_, { rejectWithValue }) => {
        try {
            const response = await api.get(`/remanufacturing_reasons/active/status`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response ? error.response.data : error.message);
        }
    }
);



const bmrRemanufacturingReasonsSlice = createSlice({
    name: 'bmrRemanufacturingReasons',
    initialState,
    reducers: {
        enableAddModal(state) {
            state.addModal = true;
        },
        disableAddModal(state) {
            state.addModal = false;
        },
    },
    extraReducers: (builder) => {
        builder
            // fetch
            .addCase(fetchAllRemanufacturingReasons.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchAllRemanufacturingReasons.fulfilled, (state, action) => {
                state.isLoading = false;
                state.allRemanufacturingReasons = action.payload.data;
            })
            .addCase(fetchAllRemanufacturingReasons.rejected, (state) => {
                state.isLoading = false;
            })
            // add
            .addCase(addRemanufacturingReason.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(addRemanufacturingReason.fulfilled, (state) => {
                state.isLoading = false;
            })
            .addCase(addRemanufacturingReason.rejected, (state) => {
                state.isLoading = false;
            })
            // edit
            .addCase(editRemanufacturingReason.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(editRemanufacturingReason.fulfilled, (state) => {
                state.isLoading = false;
            })
            .addCase(editRemanufacturingReason.rejected, (state) => {
                state.isLoading = false;
            })
            // delete
            .addCase(deleteRemanufacturingReason.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(deleteRemanufacturingReason.fulfilled, (state) => {
                state.isLoading = false;
            })
            .addCase(deleteRemanufacturingReason.rejected, (state) => {
                state.isLoading = false;
            })
            .addCase(fetchRemanufacturingReasonDropDown.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchRemanufacturingReasonDropDown.fulfilled, (state) => {
                state.isLoading = false;
            })
            .addCase(fetchRemanufacturingReasonDropDown.rejected, (state) => {
                state.isLoading = false;
            });
    },
});

export const { disableAddModal, enableAddModal } = bmrRemanufacturingReasonsSlice.actions;

export const selectRemanufacturingReasonsLoading = (state) => state.bmrRemanufacturingReasons.isLoading;
export const selectRemanufacturingReasonsAll = (state) => state.bmrRemanufacturingReasons.allRemanufacturingReasons;
export const selectBmrAddRemanufacturingReasonModal = (state) => state.bmrRemanufacturingReasons.addModal;

export default bmrRemanufacturingReasonsSlice.reducer;
